import React, { useEffect, useMemo, useState } from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getCompliancePipeline } from "api/onboardingApi";

const STEPS = [
  { key: "DOCS_UPLOAD", label: "Docs", color: "#0ea5e9" },
  { key: "TRAINING_IN_PROGRESS", label: "Training", color: "#a855f7" },
  { key: "FINAL_REVIEW", label: "Review", color: "#f59e0b" },
  { key: "READY_TO_DRIVE", label: "Ready", color: "#16a34a" },
  { key: "REJECTED", label: "Rejected", color: "#dc2626" },
];

/**
 * Onboarding pipeline strip — one count per step with click-to-filter into the
 * full pipeline page. Derives counts client-side from the existing grouped
 * /compliance/pipeline endpoint so no new backend route was needed.
 */
export const OnboardingPipelineWidget = () => {
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getCompliancePipeline();
        if (!cancelled) setPipeline(res?.value || res?.data || res);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const counts = useMemo(() => {
    if (!pipeline) return {};
    const out = {};
    STEPS.forEach((s) => {
      const v = pipeline[s.key];
      out[s.key] = Array.isArray(v) ? v.length : 0;
    });
    return out;
  }, [pipeline]);

  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  if (loading || total === 0) return null;

  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e7e7e7" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Onboarding pipeline
        </Typography>
        <Typography variant="caption" color="text.secondary">{total} drivers in flight</Typography>
      </Stack>
      <Stack direction="row" gap={1} flexWrap="wrap">
        {STEPS.map((s) => (
          <Box
            key={s.key}
            onClick={() => navigate(`/onboarding?step=${s.key}`)}
            sx={{
              cursor: "pointer",
              flex: "1 1 100px",
              minWidth: 100,
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${counts[s.key] > 0 ? s.color : "#e7e7e7"}`,
              backgroundColor: counts[s.key] > 0 ? `${s.color}10` : "transparent",
              transition: "transform 0.1s ease",
              "&:hover": { transform: "translateY(-1px)" },
            }}
          >
            <Typography variant="caption" sx={{ color: s.color, fontWeight: 600 }}>
              {s.label}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {counts[s.key] ?? 0}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

export default OnboardingPipelineWidget;
